import {Field, Form, Formik} from 'formik'
import {useState} from 'react'
import {InlineTable} from 'src/feature/bite/component'
import PlaceHolderImg from 'src/_metronic/assets/600x600.png'
import moment from 'moment'
import {useStoreOptionalInfoUpdate} from 'src/feature/store/context/useMutation'
import * as Yup from 'yup'
import {ConnectedFocusError} from 'focus-formik-error'

export default function LocationOperationalDetails({
  loading,
  detail,
  setFieldValue,
  intl,
}: {
  loading: boolean
  detail: any
  setFieldValue: any
  intl: any
}) {
  const [editable, setEditable] = useState(false)
  const {mutateAsync: updateStoreOptional, isLoading} = useStoreOptionalInfoUpdate({
    storeId: detail?.id || '',
  })

  const column = [
    [
      {
        title: 'Working Days',
        key: 'workingDays',
        required: true,
        tooltip: 'Select the days when the location is open for business.',
        dataIndex: 'workingDays',
        editable: true,
        edit: 'multiple_select',
        selects: [
          {value: '0', text: 'Sunday'},
          {value: '1', text: 'Monday'},
          {value: '2', text: 'Tuesday'},
          {value: '3', text: 'Wednesday'},
          {value: '4', text: 'Thursday'},
          {value: '5', text: 'Friday'},
          {value: '6', text: 'Saturday'},
        ],
        render: (value: any) => {
          return value?.map((item: any) => {
            return (
              <span key={item} className='badge badge-light me-1'>
                {intl.formatMessage({id: `GLOBAL.DAY.${item}`})}
              </span>
            )
          })
        },
      },

      {
        title: 'Season Start Date',
        key: 'seasonStartDate',
        dataIndex: 'seasonStartDate',
        tooltip: 'If the location has a seasonal operation, enter the start date of the season.',
        edit: 'date',
        render: (value: any) => {
          return (
            <>
              {moment().diff(moment.utc(value), 'years') > 100
                ? '-'
                : moment.utc(value).format('DD-MM-YYYY')}
            </>
          )
        },
      },
      {
        title: 'Start Time',
        key: 'openingTime',
        tooltip: 'Specify the time when the location opens for business.',
        dataIndex: 'openingTime',
        required: true,
        editable: true,
        edit: 'time',
      },
    ],
    [
      {
        title: 'Season Period',
        key: 'seasonPeriod',
        tooltip: 'Indicate whether this location operates seasonally.',
        dataIndex: 'seasonPeriod',
        yesOrNo: true,
        editable: true,
        edit: 'select',
        selects: [
          {value: true, text: 'Yes'},
          {value: false, text: 'No'},
        ],
      },
      {
        title: 'Season End Date',
        key: 'seasonEndDate',
        tooltip:
          'If the location has a seasonal operation, enter the end date of the seasonal operation.',
        dataIndex: 'seasonEndDate',
        edit: 'date',
        render: (value: any) => {
          return (
            <>
              {moment().diff(moment.utc(value), 'years') > 100
                ? '-'
                : moment.utc(value).format('DD-MM-YYYY')}
            </>
          )
        },
      },
      {
        title: 'End Time',
        key: 'closingTime',
        dataIndex: 'closingTime',
        tooltip: 'Specify the time when the location closes for business.',
        required: true,
        editable: true,
        edit: 'time',
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    workingDays: Yup.array()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    seasonPeriod: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    seasonStartDate: Yup.string().when(['seasonPeriod'], ([seasonPeriod], schema) => {
      return seasonPeriod
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .test(
              'seasonStartDate',
              intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}),
              function (value) {
                return moment(value).isAfter(moment().utc().format('YYYY-MM-DDTHH:mm:ss'))
              }
            )
        : schema.notRequired()
    }),
    seasonEndDate: Yup.string().when(['seasonPeriod'], ([seasonPeriod], schema) => {
      return seasonPeriod
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .test(
              'seasonEndDate',
              intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}),
              function (value) {
                const {seasonStartDate} = this.parent
                return moment(seasonStartDate).isBefore(value)
              }
            )
        : schema.notRequired()
    }),
    openingTime: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    closingTime: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
  })

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        webSite: detail?.Optional?.webSite,
        workingDays: detail?.Optional?.workingDays,
        seasonPeriod: detail?.Optional?.seasonPeriod,
        seasonStartDate: detail?.Optional?.seasonStartDate || '',
        seasonEndDate: detail?.Optional?.seasonEndDate || '',
        openingTime: detail?.Optional?.openingTime,
        closingTime: detail?.Optional?.closingTime,
        latitude: detail?.Optional?.latitude,
        longitude: detail?.Optional?.longitude,
      }}
      onSubmit={async (values) => {
        const updateStoreDetailResponse = await updateStoreOptional(values)
        if (updateStoreDetailResponse) {
          setFieldValue({
            ...detail,
            Optional: {
              ...detail.Optional,
              ...values,
            },
          })
        }

        setEditable(false)
      }}
      validateOnMount
    >
      {({errors, resetForm, handleSubmit, values}) => {
        return (
          <Form>
            <ConnectedFocusError />
            <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
              <div className='card-header'>
                <div className='card-title m-0 d-flex justify-content-between w-100'>
                  <div>
                    <h3 className='fw-bold mb-2 mt-5'>Operational Details</h3>
                    <p className='text-muted fw-normal fs-6'>
                      {intl.formatMessage({id: 'PROVIDER.AVAILABILITY_SERVICES_DESCRIPTION'})}
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body p-9'>
                <InlineTable
                  loading={loading}
                  dataSource={detail.Optional}
                  columns={column}
                  editable={editable}
                  values={values}
                />
              </div>
              <div className='card-footer d-flex w-100 justify-content-end'>
                {editable ? (
                  <div>
                    <button
                      className='btn btn-primary'
                      style={{marginRight: 10}}
                      onClick={() => {
                        setEditable(false)
                        resetForm()
                      }}
                    >
                      <i className='bi bi-x me-2'></i>
                      {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
                    </button>
                    <button
                      className='btn btn-primary'
                      type='button'
                      disabled={loading || isLoading}
                      onClick={async (e) => {
                        e.preventDefault()

                        await handleSubmit()
                      }}
                    >
                      <i className='bi bi-save2 me-2'></i>
                      {loading
                        ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                        : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
                    </button>
                  </div>
                ) : (
                  <button className='btn btn-primary' onClick={() => setEditable(true)}>
                    <i className='bi bi-pencil me-2'></i>
                    {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
                  </button>
                )}
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
